import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { api } from "../api";

const EMPTY_FORM = {
  name: "",
  aliases: "",
  geography: "",
  period: "",
  socialSetting: "",
  visualAtmosphere: "",
  importantRules: "",
  notes: "",
};

function clean(value) {
  return value == null
    ? ""
    : String(value).trim();
}

function searchText(location) {
  return [
    location?.name,
    location?.aliases,
    location?.geography,
    location?.period,
    location?.socialSetting,
    location?.visualAtmosphere,
    location?.importantRules,
    location?.notes,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function LocationCard({
  location,
  canManage,
  isFR,
  onEdit,
  onArchive,
  onRestore,
}) {
  const archived =
    clean(location?.status).toLowerCase() ===
    "archived";

  return (
    <article
      className={`screenplay-location-record${
        archived
          ? " screenplay-location-record--archived"
          : ""
      }`}
      style={{
        marginBottom: 10,
        padding: "14px 16px",
        border: archived
          ? "1px dashed #cbd5e1"
          : "1px solid #dbe7df",
        borderLeft: archived
          ? "4px solid #94a3b8"
          : "4px solid #0f766e",
        borderRadius: 12,
        background: archived
          ? "#f8fafc"
          : "#ffffff",
        opacity: archived ? 0.82 : 1,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h4
            style={{
              margin: "0 0 4px",
              color: "#0f172a",
            }}
          >
            {location?.name ||
              (isFR
                ? "Lieu sans nom"
                : "Unnamed location")}
          </h4>

          <p
            className="ns-card-meta"
            style={{ margin: 0 }}
          >
            {location?.geography ||
              location?.period ||
              (isFR
                ? "Contexte non défini"
                : "Context not defined")}

            {archived
              ? ` · ${
                  isFR
                    ? "Archivé"
                    : "Archived"
                }`
              : ""}
          </p>
        </div>

        {canManage ? (
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {!archived ? (
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() =>
                  onEdit(location)
                }
              >
                {isFR ? "Modifier" : "Edit"}
              </button>
            ) : null}

            {archived ? (
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() =>
                  onRestore(location)
                }
              >
                {isFR
                  ? "Restaurer"
                  : "Restore"}
              </button>
            ) : (
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() =>
                  onArchive(location)
                }
              >
                {isFR
                  ? "Archiver"
                  : "Archive"}
              </button>
            )}
          </div>
        ) : null}
      </div>

      {location?.visualAtmosphere ? (
        <p
          style={{
            margin: "10px 0 0",
            lineHeight: 1.5,
            color: "#334155",
          }}
        >
          <strong>
            {isFR
              ? "Atmosphère visuelle"
              : "Visual atmosphere"}
            :
          </strong>{" "}
          {location.visualAtmosphere}
        </p>
      ) : null}

      {location?.importantRules ? (
        <p
          style={{
            margin: "8px 0 0",
            lineHeight: 1.5,
            color: "#334155",
          }}
        >
          <strong>
            {isFR
              ? "Règles de continuité"
              : "Continuity rules"}
            :
          </strong>{" "}
          {location.importantRules}
        </p>
      ) : null}
    </article>
  );
}

function TextField({
  label,
  help,
  value,
  onChange,
  multiline = false,
  required = false,
}) {
  return (
    <label
      style={{
        display: "grid",
        gap: 6,
      }}
    >
      <span
        style={{
          fontWeight: 700,
          color: "#334155",
        }}
      >
        {label}
        {required ? " *" : ""}
      </span>

      {help ? (
        <span
          className="ns-card-meta"
          style={{
            margin: 0,
            lineHeight: 1.4,
          }}
        >
          {help}
        </span>
      ) : null}

      {multiline ? (
        <textarea
          className="ns-input"
          rows={3}
          value={value}
          onChange={onChange}
        />
      ) : (
        <input
          className="ns-input"
          value={value}
          onChange={onChange}
        />
      )}
    </label>
  );
}

export default function ScreenplayLocationsWorld({
  projectId,
  canManage = false,
  active = false,
  isFR = false,
}) {
  const id =
    clean(projectId);

  const formRef =
    useRef(null);

  const archiveCancelRef =
    useRef(null);

  const [
    locations,
    setLocations,
  ] = useState([]);

  const [
    includeArchived,
    setIncludeArchived,
  ] = useState(false);

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    message,
    setMessage,
  ] = useState("");

  const [
    editing,
    setEditing,
  ] = useState(null);

  const [
    pendingArchive,
    setPendingArchive,
  ] = useState(null);

  const [
    form,
    setForm,
  ] = useState(EMPTY_FORM);

  const activeLocations =
    useMemo(
      () =>
        locations.filter(
          (location) =>
            clean(
              location?.status
            ).toLowerCase() !==
            "archived"
        ),
      [locations]
    );

  const archivedLocations =
    useMemo(
      () =>
        locations.filter(
          (location) =>
            clean(
              location?.status
            ).toLowerCase() ===
            "archived"
        ),
      [locations]
    );

  const visibleLocations =
    useMemo(() => {
      const query =
        clean(search).toLowerCase();

      return locations.filter(
        (location) => {
          const archived =
            clean(
              location?.status
            ).toLowerCase() ===
            "archived";

          if (
            archived &&
            !includeArchived
          ) {
            return false;
          }

          if (!query) {
            return true;
          }

          return searchText(
            location
          ).includes(query);
        }
      );
    }, [
      locations,
      includeArchived,
      search,
    ]);

  async function loadLocations() {
    if (!id) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload =
        await api.get(
          `/api/projects/${encodeURIComponent(
            id
          )}/locations?includeArchived=true`
        );

      setLocations(
        Array.isArray(
          payload?.locations
        )
          ? payload.locations
          : []
      );
    } catch (loadError) {
      setError(
        clean(
          loadError?.message
        ) ||
          (
            isFR
              ? "Impossible de charger les lieux."
              : "Could not load locations."
          )
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (
      !active ||
      !id
    ) {
      return;
    }

    loadLocations();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    id,
  ]);

  useEffect(() => {
    if (!pendingArchive) {
      return undefined;
    }

    const focusTimer =
      window.setTimeout(() => {
        archiveCancelRef.current
          ?.focus();
      }, 0);

    function handleKeyDown(
      event
    ) {
      if (
        event.key === "Escape" &&
        !saving
      ) {
        setPendingArchive(null);
      }
    }

    document.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.clearTimeout(
        focusTimer
      );

      document.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [
    pendingArchive,
    saving,
  ]);

  function updateField(
    key,
    value
  ) {
    setForm(
      (current) => ({
        ...current,
        [key]: value,
      })
    );
  }

  function resetForm({
    preserveMessage = false,
  } = {}) {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError("");

    if (!preserveMessage) {
      setMessage("");
    }
  }

  function startEdit(
    location
  ) {
    setEditing(location);

    setForm({
      name:
        location?.name || "",
      aliases:
        location?.aliases || "",
      geography:
        location?.geography || "",
      period:
        location?.period || "",
      socialSetting:
        location?.socialSetting || "",
      visualAtmosphere:
        location?.visualAtmosphere || "",
      importantRules:
        location?.importantRules || "",
      notes:
        location?.notes || "",
    });

    setError("");
    setMessage("");

    setTimeout(() => {
      if (
        formRef.current &&
        typeof formRef.current
          .scrollIntoView ===
          "function"
      ) {
        formRef.current
          .scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
      }
    }, 50);
  }

  async function saveLocation(
    event
  ) {
    event.preventDefault();

    if (!id) {
      return;
    }

    if (
      !clean(form.name)
    ) {
      setError(
        isFR
          ? "Le nom du lieu est obligatoire."
          : "Location name is required."
      );
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      if (
        editing?.id ||
        editing?._id
      ) {
        const locationId =
          clean(
            editing.id ||
            editing._id
          );

        await api.put(
          `/api/projects/${encodeURIComponent(
            id
          )}/locations/${encodeURIComponent(
            locationId
          )}`,
          form
        );

        resetForm({
          preserveMessage: true,
        });

        setMessage(
          isFR
            ? "Lieu mis à jour."
            : "Location updated."
        );
      } else {
        await api.post(
          `/api/projects/${encodeURIComponent(
            id
          )}/locations`,
          form
        );

        resetForm({
          preserveMessage: true,
        });

        setMessage(
          isFR
            ? "Lieu ajouté."
            : "Location added."
        );
      }

      await loadLocations();
    } catch (saveError) {
      setError(
        clean(
          saveError?.message
        ) ||
          (
            isFR
              ? "Impossible d’enregistrer le lieu."
              : "Could not save location."
          )
      );
    } finally {
      setSaving(false);
    }
  }

  function archiveLocation(
    location
  ) {
    setError("");
    setMessage("");
    setPendingArchive(
      location
    );
  }

  async function confirmArchiveLocation() {
    const locationId =
      clean(
        pendingArchive?.id ||
        pendingArchive?._id
      );

    if (
      !id ||
      !locationId ||
      saving
    ) {
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await api.patch(
        `/api/projects/${encodeURIComponent(
          id
        )}/locations/${encodeURIComponent(
          locationId
        )}/archive`,
        {}
      );

      setPendingArchive(null);

      setMessage(
        isFR
          ? "Lieu archivé."
          : "Location archived."
      );

      await loadLocations();
    } catch (archiveError) {
      setError(
        clean(
          archiveError?.message
        ) ||
          (
            isFR
              ? "Impossible d’archiver le lieu."
              : "Could not archive location."
          )
      );
    } finally {
      setSaving(false);
    }
  }

  async function restoreLocation(
    location
  ) {
    const locationId =
      clean(
        location?.id ||
        location?._id
      );

    if (
      !id ||
      !locationId
    ) {
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await api.patch(
        `/api/projects/${encodeURIComponent(
          id
        )}/locations/${encodeURIComponent(
          locationId
        )}/restore`,
        {}
      );

      setMessage(
        isFR
          ? "Lieu restauré."
          : "Location restored."
      );

      await loadLocations();
    } catch (restoreError) {
      setError(
        clean(
          restoreError?.message
        ) ||
          (
            isFR
              ? "Impossible de restaurer le lieu."
              : "Could not restore location."
          )
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      className="ns-card screenplay-locations-world"
      style={{
        marginTop: 18,
        padding: 20,
        borderRadius: 20,
      }}
    >
      {pendingArchive ? (
        <div
          className="screenplay-location-archive-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            display: "grid",
            placeItems: "center",
            padding: 20,
            background:
              "rgba(15,23,42,0.58)",
          }}
        >
          <section
            className="screenplay-location-archive-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="screenplay-location-archive-title"
            aria-describedby="screenplay-location-archive-description"
            style={{
              width:
                "min(100%, 520px)",
              padding: 24,
              border:
                "1px solid #e2e8f0",
              borderRadius: 18,
              background: "#ffffff",
              boxShadow:
                "0 24px 70px rgba(15,23,42,0.28)",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.1em",
                textTransform:
                  "uppercase",
                color: "#b45309",
              }}
            >
              {isFR
                ? "ARCHIVER LE LIEU"
                : "ARCHIVE LOCATION"}
            </div>

            <h3
              id="screenplay-location-archive-title"
              style={{
                margin: "7px 0 8px",
                color: "#0f172a",
              }}
            >
              {isFR
                ? `Archiver « ${
                    pendingArchive?.name ||
                    "ce lieu"
                  } » ?`
                : `Archive ${
                    pendingArchive?.name ||
                    "this location"
                  }?`}
            </h3>

            <p
              id="screenplay-location-archive-description"
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.55,
              }}
            >
              {isFR
                ? "Ce lieu quittera la liste active. Il ne sera pas supprimé définitivement et pourra être restauré plus tard."
                : "This location will move out of the active list. It will not be permanently deleted and can be restored later."}
            </p>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent:
                  "flex-end",
                gap: 10,
                marginTop: 22,
              }}
            >
              <button
                ref={archiveCancelRef}
                type="button"
                className="ns-button ns-button-outline"
                disabled={saving}
                onClick={() =>
                  setPendingArchive(null)
                }
              >
                {isFR
                  ? "Annuler"
                  : "Cancel"}
              </button>

              <button
                type="button"
                className="ns-button"
                disabled={saving}
                onClick={
                  confirmArchiveLocation
                }
                style={{
                  background: "#b45309",
                  borderColor: "#b45309",
                  color: "#ffffff",
                }}
              >
                {saving
                  ? (
                      isFR
                        ? "Archivage…"
                        : "Archiving…"
                    )
                  : (
                      isFR
                        ? "Archiver le lieu"
                        : "Archive location"
                    )}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      <div
        style={{
          fontSize: 12,
          fontWeight: 900,
          letterSpacing: "0.1em",
          textTransform:
            "uppercase",
          color: "#0f766e",
        }}
      >
        {isFR
          ? "LIEUX"
          : "LOCATIONS"}
      </div>

      <h3
        className="ns-card-title"
        style={{
          margin: "5px 0 6px",
        }}
      >
        {isFR
          ? "Lieux du scénario"
          : "Screenplay Locations"}
      </h3>

      <p
        className="ns-card-meta"
        style={{
          margin: 0,
          lineHeight: 1.55,
        }}
      >
        {isFR
          ? "Préservez la géographie, la période, le contexte social, l’atmosphère visuelle et les règles importantes propres à chaque lieu."
          : "Preserve geography, period, social setting, visual atmosphere and important continuity rules for each location."}
      </p>

      <div
        style={{
          marginTop: 18,
          display: "grid",
          gridTemplateColumns:
            "minmax(0, 1fr) auto",
          gap: 14,
          alignItems: "end",
        }}
      >
        <label
          style={{
            display: "grid",
            gap: 6,
          }}
        >
          <strong>
            {isFR
              ? "Trouver un lieu"
              : "Find a location"}
          </strong>

          <input
            className="ns-input"
            type="search"
            value={search}
            onChange={(event) =>
              setSearch(
                event.target.value
              )
            }
            placeholder={
              isFR
                ? "Nom, géographie, période, règle…"
                : "Name, geography, period, rule…"
            }
          />
        </label>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            paddingBottom: 10,
          }}
        >
          <input
            type="checkbox"
            checked={
              includeArchived
            }
            onChange={(event) =>
              setIncludeArchived(
                event.target.checked
              )
            }
          />

          <span>
            {isFR
              ? "Afficher les archives"
              : "Show archived"}
          </span>
        </label>
      </div>

      <p
        className="ns-card-meta"
        style={{
          margin: "10px 0 0",
        }}
      >
        {activeLocations.length}{" "}
        {isFR ? "actif(s)" : "active"} ·{" "}
        {archivedLocations.length}{" "}
        {isFR
          ? "archivé(s)"
          : "archived"}{" "}
        · {visibleLocations.length}{" "}
        {isFR
          ? "affiché(s)"
          : "shown"}
      </p>

      {message ? (
        <div
          className="ns-card ns-card-soft"
          style={{
            marginTop: 14,
            borderColor:
              "#bbf7d0",
          }}
        >
          {message}
        </div>
      ) : null}

      {error ? (
        <div
          className="ns-card ns-card-soft"
          style={{
            marginTop: 14,
            borderColor:
              "#fecaca",
            color: "#991b1b",
          }}
        >
          {error}
        </div>
      ) : null}

      <div
        className="screenplay-location-list"
        style={{
          marginTop: 18,
          paddingTop: 16,
          borderTop:
            "1px solid #e2e8f0",
        }}
      >
        <h4
          style={{
            margin: "0 0 12px",
          }}
        >
          {isFR ? "Lieux" : "Locations"}
        </h4>

        {loading ? (
          <p className="ns-card-meta">
            {isFR
              ? "Chargement…"
              : "Loading…"}
          </p>
        ) : visibleLocations.length ===
          0 ? (
          <p className="ns-card-meta">
            {isFR
              ? "Aucun lieu ne correspond à cette vue."
              : "No locations match this view."}
          </p>
        ) : (
          visibleLocations.map(
            (location) => (
              <LocationCard
                key={
                  location.id ||
                  location._id
                }
                location={location}
                canManage={
                  canManage
                }
                isFR={isFR}
                onEdit={
                  startEdit
                }
                onArchive={
                  archiveLocation
                }
                onRestore={
                  restoreLocation
                }
              />
            )
          )
        )}
      </div>

      {canManage ? (
        <form
          ref={formRef}
          className="screenplay-location-form"
          onSubmit={
            saveLocation
          }
          style={{
            marginTop: 22,
            padding: 18,
            border:
              "1px solid #dbe7df",
            borderTop:
              "2px solid #5f7f72",
            borderRadius: 14,
            background: "#f8fafc",
          }}
        >
          <h4
            style={{
              margin: "0 0 16px",
            }}
          >
            {editing
              ? (
                  isFR
                    ? `Modifier : ${
                        editing.name
                      }`
                    : `Edit: ${
                        editing.name
                      }`
                )
              : (
                  isFR
                    ? "Ajouter un lieu"
                    : "Add location"
                )}
          </h4>

          <div
            style={{
              display: "grid",
              gap: 16,
            }}
          >
            <TextField
              label={
                isFR
                  ? "Nom"
                  : "Name"
              }
              required
              value={form.name}
              onChange={(event) =>
                updateField(
                  "name",
                  event.target.value
                )
              }
            />

            <TextField
              label={
                isFR
                  ? "Autres noms"
                  : "Aliases"
              }
              help={
                isFR
                  ? "Autres noms utilisés dans le scénario ou par les personnages."
                  : "Other names used in the screenplay or by characters."
              }
              value={form.aliases}
              onChange={(event) =>
                updateField(
                  "aliases",
                  event.target.value
                )
              }
            />

            <TextField
              label={
                isFR
                  ? "Géographie"
                  : "Geography"
              }
              help={
                isFR
                  ? "Village, ville, région, distances, terrain ou autres repères physiques utiles."
                  : "Village, city, region, distances, terrain or other useful physical anchors."
              }
              multiline
              value={
                form.geography
              }
              onChange={(event) =>
                updateField(
                  "geography",
                  event.target.value
                )
              }
            />

            <TextField
              label={
                isFR
                  ? "Période"
                  : "Period"
              }
              help={
                isFR
                  ? "Époque, saison ou autre contexte temporel propre au lieu."
                  : "Era, season or other time context attached to this place."
              }
              multiline
              value={form.period}
              onChange={(event) =>
                updateField(
                  "period",
                  event.target.value
                )
              }
            />

            <TextField
              label={
                isFR
                  ? "Contexte social"
                  : "Social setting"
              }
              help={
                isFR
                  ? "Communauté, statut, usages ou conditions sociales qui façonnent le lieu."
                  : "Community, status, customs or social conditions shaping the place."
              }
              multiline
              value={
                form.socialSetting
              }
              onChange={(event) =>
                updateField(
                  "socialSetting",
                  event.target.value
                )
              }
            />

            <TextField
              label={
                isFR
                  ? "Atmosphère visuelle"
                  : "Visual atmosphere"
              }
              help={
                isFR
                  ? "Lumière, texture, architecture et impression générale à préserver."
                  : "Light, texture, architecture and overall visual feeling to preserve."
              }
              multiline
              value={
                form.visualAtmosphere
              }
              onChange={(event) =>
                updateField(
                  "visualAtmosphere",
                  event.target.value
                )
              }
            />

            <TextField
              label={
                isFR
                  ? "Règles importantes de continuité"
                  : "Important continuity rules"
              }
              help={
                isFR
                  ? "Faits établis sur ce lieu que les scènes ultérieures ne doivent pas contredire à la légère."
                  : "Established facts about this location that later scenes should not casually contradict."
              }
              multiline
              value={
                form.importantRules
              }
              onChange={(event) =>
                updateField(
                  "importantRules",
                  event.target.value
                )
              }
            />

            <TextField
              label={
                isFR
                  ? "Notes"
                  : "Notes"
              }
              help={
                isFR
                  ? "Autres faits utiles sur le lieu."
                  : "Other useful facts about the location."
              }
              multiline
              value={form.notes}
              onChange={(event) =>
                updateField(
                  "notes",
                  event.target.value
                )
              }
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 18,
            }}
          >
            <button
              type="submit"
              className="ns-button"
              disabled={saving}
            >
              {saving
                ? (
                    isFR
                      ? "Enregistrement…"
                      : "Saving…"
                  )
                : editing
                ? (
                    isFR
                      ? "Enregistrer les modifications"
                      : "Save changes"
                  )
                : (
                    isFR
                      ? "Ajouter le lieu"
                      : "Add location"
                  )}
            </button>

            <button
              type="button"
              className="ns-button ns-button-outline"
              disabled={saving}
              onClick={() =>
                resetForm()
              }
            >
              {editing
                ? (
                    isFR
                      ? "Annuler"
                      : "Cancel"
                  )
                : (
                    isFR
                      ? "Effacer le formulaire"
                      : "Clear form"
                  )}
            </button>
          </div>
        </form>
      ) : (
        <div
          className="ns-card ns-card-soft"
          style={{
            marginTop: 16,
          }}
        >
          <p
            className="ns-card-meta"
            style={{ margin: 0 }}
          >
            {isFR
              ? "Vous pouvez consulter les lieux. Seul le propriétaire du projet peut modifier ces fiches."
              : "You can read the Locations records. Only the project Owner can change these records."}
          </p>
        </div>
      )}

      <div
        className="screenplay-location-ai-note"
        role="note"
        style={{
          marginTop: 16,
          padding: "12px 14px",
          borderLeft:
            "4px solid #94a3b8",
          borderRadius: 10,
          background: "#f8fafc",
        }}
      >
        <strong>
          {isFR
            ? "Continuité assistée, plus tard"
            : "Assisted continuity, later"}
        </strong>

        <p
          className="ns-card-meta"
          style={{
            margin: "5px 0 0",
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Ces fiches pourront plus tard aider à signaler des contradictions possibles. L’IA restera consultative et ne modifiera ni ces fiches ni le Canon."
            : "These records may later help flag possible contradictions. AI will remain advisory and will not change these records or Canon."}
        </p>
      </div>
    </section>
  );
}
